import { memo } from "react";
import type { TagResponseDTO } from "../types";

interface TagBadgeProps {
  /** Aceita qualquer objeto com `name` e `color` — não exige o DTO completo. */
  tag: Pick<TagResponseDTO, "name" | "color">;
  size?: "sm" | "md";
}

/**
 * Exibe uma tag como pill colorida com dot indicador.
 *
 * A cor de fundo e borda são derivadas de `tag.color` com transparência via
 * sufixo hexadecimal (`20` ≈ 12%, `40` ≈ 25%), sem depender de classes Tailwind
 * arbitrárias que exigiriam safelist em produção.
 *
 * Reutilizável em qualquer contexto — aceita qualquer objeto com `name` e `color`.
 *
 * @example
 * <TagBadge tag={{ name: "Premium", color: "#FF5733" }} />
 * <TagBadge tag={tag} size="sm" />
 */
export const TagBadge = memo(function TagBadge({
  tag,
  size = "md",
}: TagBadgeProps) {
  const padding = size === "sm" ? "px-2 py-0.5 text-xs" : "px-3 py-1 text-sm";

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-medium ${padding}`}
      style={{
        backgroundColor: `${tag.color}20`,
        color: tag.color,
        border: `1px solid ${tag.color}40`,
      }}
    >
      <span
        className="w-2 h-2 rounded-full shrink-0"
        style={{ backgroundColor: tag.color }}
      />
      {tag.name}
    </span>
  );
});
